import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useOnboarding } from "../../context/OnboardingContext";
import { useRBAC } from "../../context/RBACContext";
import { ApiHelper } from "../../utils/ApiHelper";
import { CREATE_COMPANY, CREATE_USER, CREATE_INVITE, SEND_INVITE } from "../../lib/API_KEYS";

export default function VerificationSuccess() {
  const navigate = useNavigate();
  const { cognitoData } = useOnboarding();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasRunRef = useRef(false); // prevent double execution in strict mode
  const { initializeRBAC } = useRBAC();

  useEffect(() => {
    if (!cognitoData || hasRunRef.current) return;
    hasRunRef.current = true;

    const saveDataToBackend = async () => {
      try {
        console.log("✅ Starting verification success process...");
        console.log("Cognito data:", cognitoData);

        if (!cognitoData.password) {
          toast.error("Password missing! Cannot sign in.");
          setIsProcessing(false);
          return;
        }

        // 🔑 Sign in main user
        const session = await Auth.signIn(cognitoData.email, cognitoData.password);
        console.log("User signed in:", session);

        // Load role & permissions immediately after login
        await initializeRBAC();

        const token = session.signInUserSession.idToken.jwtToken;
        const headers = { Authorization: `Bearer ${token}` };

        // 0️⃣ Create company if not already set
        let companyId = cognitoData.companyId;
        if (!companyId) {
          const payload = {
            name: cognitoData.companyName || " ",
            industry: cognitoData.industry || [],
            otherIndustry: cognitoData.otherIndustry || "",
            domain: cognitoData.email.trim().toLowerCase().split("@")[1] || " ",
          };
          const companyResp = await new ApiHelper().FetchFromServer(
            ...CREATE_COMPANY,
            undefined,
            payload,
            headers
          );
          companyId = companyResp.data._id;
          console.log("🏢 Company created:", companyId);
        }

        // 1️⃣ Save main user
        const payload = {
          // profilePic: null,
          // country: null,
          // city: null,
          // postcode: null,
          email: cognitoData.email,
          name: cognitoData.fullName || "",
          role: cognitoData.role,
          companyName: cognitoData.companyName || "",
          companyId,
          // industry: cognitoData.industry || [],
          // otherIndustry: cognitoData.otherIndustry || "",
        }
        await new ApiHelper().FetchFromServer(
          ...CREATE_USER,
          undefined,
          payload,
          headers
        );
        console.log("👤 Main user saved");

        // 2️⃣ If remindLater is NOT set, create invites
        if (!cognitoData.remindLater) {
          for (const member of cognitoData.team || []) {
            if (!member.email) continue;
            const payload = {
              // profilePic: null,
              // country: null,
              // city: null,
              // name: null,
              // postcode: null,
              email: member.email,
              role: member.role,
              companyId,
              companyName: member.companyName || "",
              addedBy: cognitoData.email,
              // industry: cognitoData.industry || [],
              status: "pending",
            }
            const inviteResp = await new ApiHelper().FetchFromServer(
              ...CREATE_INVITE,
              undefined,
              payload,
              headers
            );
            console.log("✉️ Invite created:", inviteResp.data);

            await new ApiHelper().FetchFromServer(
              ...SEND_INVITE,
              undefined,
              { email: member.email, inviteId: inviteResp.data._id },
              headers
            );
            console.log("📨 Invite email sent to:", member.email);
          }
        } else {
          console.log("⏳ Remind me later chosen: skipping invites");
        }

        toast.success("🎉 Account saved successfully!");
      } catch (err) {
        console.error("❌ Error saving backend data:", err.response || err);
        toast.error("Failed to save user or send invites. Check console logs.");
      } finally {
        setIsProcessing(false);
      }
    };


    saveDataToBackend();
  }, [cognitoData]);

  const handleContinue = () => navigate("/preloader");

  return (
    <div className="d-flex vh-100">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div className="container-fluid px-3">
          <div className="row vh-100">
            <div className="col-12 d-flex justify-content-center align-items-center">
              <div className="text-center" style={{ maxWidth: "420px", width: "100%" }}>
                <div className="success-icon-svg mb-4 mx-auto">
                  <img src="/images/success.svg" alt="check_icon" />
                </div>

                <h4 className="fw-bold" style={{ fontFamily: "Satoshi", lineHeight: "1.4" }}>
                  {isProcessing ? "Processing..." : "Verified"}
                </h4>

                <p className="text-muted mt-2 mb-4" style={{ fontSize: "14px" }}>
                  {isProcessing
                    ? "Saving your account and sending team invites..."
                    : "You have been successfully verified."}
                </p>

                {!isProcessing && (
                  <button
                    onClick={handleContinue}
                    className="btn btn-primary w-100"
                    style={{ backgroundColor: "#1A1A52", fontSize: "16px", fontWeight: "600", height: "40px", border: "none" }}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>

          <style>{`
            .success-icon-svg {
              width: 64px;
              height: 64px;
              background-color: #ffffff;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 0 10px rgba(0,0,0,0.05);
            }
          `}</style>
        </div>
      </div>

      <div className="d-none d-md-flex align-items-center justify-content-center" style={{ width: "50%" }}>
        <img src="/images/BG.png" alt="Side" className="img-fluid" style={{ maxHeight: "90%", maxWidth: "90%", objectFit: "contain" }} />
      </div>

      <ToastContainer />
    </div>
  );
}

